import React, { createContext, useContext, useEffect } from 'react';
import { Drawer } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useLocation, useNavigate, type Location } from 'react-router-dom';

type RouterDrawerProps = {
    title: string;
    /** Path to navigate to when the drawer closes without a background location. */
    closeTo: string;
    children: React.ReactNode;
    size?: string | number;
};

export type RouterDrawerLocationState = {
    /** Page to restore when the drawer closes (overlay mode). */
    backgroundLocation?: Location;
};

const RouterDrawerCloseContext = createContext<(() => void) | null>(null);

/**
 * Close the surrounding {@link RouterDrawer} (exit animation, then navigate away).
 * Prefer this over `navigate(...)` so the drawer dismisses like other Mantine overlays.
 */
export function useRouterDrawerClose(): () => void {
    const close = useRouterDrawerCloseOptional();
    if (!close) {
        throw new Error('useRouterDrawerClose must be used within a RouterDrawer');
    }
    return close;
}

/** Returns null when not rendered inside a {@link RouterDrawer}. */
export function useRouterDrawerCloseOptional(): (() => void) | null {
    return useContext(RouterDrawerCloseContext);
}

/**
 * Open a router-tied drawer as an overlay on the current page.
 * The URL updates for deep linking; closing restores the page you were on.
 */
export function useOpenRouterDrawer() {
    const navigate = useNavigate();
    const location = useLocation();

    return (path: string) =>
        navigate(path, { state: { backgroundLocation: location } satisfies RouterDrawerLocationState });
}

function drawerReturnPath(location: Location, closeTo: string): string {
    const backgroundLocation = (location.state as RouterDrawerLocationState | null)?.backgroundLocation;
    if (backgroundLocation) {
        return `${backgroundLocation.pathname}${backgroundLocation.search}${backgroundLocation.hash}`;
    }
    return closeTo;
}

/**
 * Mantine Drawer tied to the router: opens on mount; closing animates out then navigates away.
 * Use with routes like `/components/new` so deep links open the drawer.
 */
const RouterDrawer: React.FC<RouterDrawerProps> = ({ title, closeTo, children, size = 'lg' }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [opened, { open, close }] = useDisclosure(false);

    useEffect(() => {
        open();
    }, [open]);

    return (
        <Drawer
            opened={opened}
            onClose={close}
            onExitTransitionEnd={() => navigate(drawerReturnPath(location, closeTo), { replace: true })}
            title={title}
            position="right"
            size={size}
            padding="md"
            overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
            closeButtonProps={{ 'aria-label': 'Close drawer' }}
        >
            <RouterDrawerCloseContext.Provider value={close}>
                {children}
            </RouterDrawerCloseContext.Provider>
        </Drawer>
    );
};

export default RouterDrawer;
